import { css, keyframes, SerializedStyles } from '@emotion/react';

const incrementFloat = 0.00001;

const typewriterTimings = {
  collapsed: {
    start: 0,
    end: 70,
    rules: css`
      padding-right: 0;
      width: 0;
    `,
  },
  expanded: {
    start: 70,
    end: 100,
    rules: css`
      padding-right: 1rem;
      width: 100%;
    `,
  },
};

const cursorTimings = {
  idle: {
    start: 0,
    end: 70,
    rules: css`
      opacity: 1;
    `,
  },
  disappear: {
    start: 75,
    end: 90,
    rules: css`
      opacity: 0;
    `,
  },
  reappear: {
    start: 95,
    end: 100,
    rules: css`
      opacity: 1;
    `,
  },
};

const pulseTimings = {
  shown: {
    start: 0,
    end: 37.5,
    rules: css`
      opacity: 1;
    `,
  },
  disappear: {
    start: 50,
    end: 87.5,
    rules: css`
      opacity: 0;
    `,
  },
  reappear: {
    start: 100,
    end: 100,
    rules: css`
      opacity: 1;
    `,
  },
};

const pulseDuration = 50;

export const composeCtaAnimationData = ({
  ctasCount,
  finalItemIterations,
  baseAnimationMs,
}: {
  ctasCount: number;
  finalItemIterations: number;
  baseAnimationMs: number;
}) => {
  const regularItems = ctasCount - 1;
  const totalIterations = regularItems + finalItemIterations;

  const iterationAnimationMs = baseAnimationMs * 2;
  const totalAnimationMs = totalIterations * iterationAnimationMs;

  const currentCompression = 1 / (totalIterations * 2);

  const iterationDuration = 100 / totalIterations;
  const animationDuration = iterationDuration / 2;

  const finalItemDuration = (finalItemIterations / totalIterations) * 100;

  const finalItemEndAnimationStart = finalItemDuration - animationDuration;

  const finalItemAnimationDelay = (finalItemEndAnimationStart / 100) * totalAnimationMs;

  const delays = [
    ...[...new Array(regularItems)].map(
      (_, index) => baseAnimationMs + index * iterationAnimationMs
    ),
    -finalItemAnimationDelay,
  ];

  const interpolate = (interpolationCallback: () => SerializedStyles) => interpolationCallback();

  const typewriterAnimation = keyframes` 
    ${interpolate(() => {
      const { collapsed, expanded } = typewriterTimings;

      return css`
        ${collapsed.start}% {
          ${collapsed.rules}
        }

        ${expanded.start}%,
        ${expanded.end}% {
          ${expanded.rules}
        }
      `;
    })}
  `;

  const pulseAnimationDurationMs = baseAnimationMs * (pulseDuration / 100);

  const pulseAnimation = keyframes`
    ${interpolate(() => {
      const { shown, disappear, reappear } = pulseTimings;

      return css`
        ${shown.start}%,
        ${shown.end}% {
          ${shown.rules}
        }

        ${disappear.start}%,
        ${disappear.end}% {
          ${disappear.rules}
        }

        ${reappear.start}% {
          ${reappear.rules}
        }
      `;
    })}
  `;

  const cursorAnimation = keyframes` 
    ${interpolate(() => {
      const { idle, disappear, reappear } = cursorTimings;

      return css`
        ${idle.start}%,
        ${idle.end}% {
          ${idle.rules}
        }

        ${disappear.start}%,
        ${disappear.end}% {
          ${disappear.rules}
        }

        ${reappear.start}%,
        ${reappear.end}% {
          ${reappear.rules}
        }
      `;
    })}
  `;

  const staggerAnimation = keyframes` 
    ${interpolate(() => {
      return css`
        0%,
        ${iterationDuration - incrementFloat}% {
          opacity: 1;
        }

        ${iterationDuration}%,
        100% {
          opacity: 0;
        }
      `;
    })}
  `;

  const finalTypewriterAnimation = keyframes`
    ${interpolate(() => {
      const { collapsed, expanded } = typewriterTimings;

      return css`
        0% {
          ${collapsed.rules}
        }

        ${expanded.start * currentCompression}%,
        ${finalItemEndAnimationStart}% {
          ${expanded.rules}
        }

        ${finalItemEndAnimationStart + expanded.start * currentCompression}%,
        100% {
          ${collapsed.rules}
        }
      `;
    })}
  `;

  const pulsesCss = [
    () => {
      const { idle, disappear, reappear } = cursorTimings;

      return css`
        ${idle.start * currentCompression}%,
        ${idle.end * currentCompression}% {
          ${idle.rules}
        }

        ${disappear.start * currentCompression}%,
        ${disappear.end * currentCompression}% {
          ${disappear.rules}
        }

        ${reappear.start * currentCompression}%,
        ${reappear.end * currentCompression - incrementFloat}% {
          ${reappear.rules}
        }
      `;
    },
    ...(() => {
      const baseWeight = pulseDuration / 100;

      const compressedAnimationDuration = pulseDuration * currentCompression;

      const pulsesDuration = finalItemEndAnimationStart - animationDuration;

      const unroundedPulseCount = pulsesDuration / compressedAnimationDuration;

      const pulseCount = Math.round(unroundedPulseCount);

      const pulseAnimationWeight = pulseCount / unroundedPulseCount;

      return [...new Array(pulseCount)].map((_, index) => {
        const pulseWeight = baseWeight * pulseAnimationWeight * currentCompression;

        const { shown, disappear, reappear } = pulseTimings;

        const delay = animationDuration + reappear.end * pulseWeight * index;

        return () => css`
          ${delay + shown.start * pulseWeight}%,
          ${delay + shown.end * pulseWeight}% {
            ${shown.rules}
          }

          ${delay + disappear.start * pulseWeight}%,
          ${delay + disappear.end * pulseWeight}% {
            ${disappear.rules}
          }

          ${delay + reappear.start * pulseWeight}% {
            ${reappear.rules}
          }
        `;
      });
    })(),
    () => {
      const { idle, disappear, reappear } = cursorTimings;

      return css`
        ${(100 - reappear.end) * currentCompression + finalItemEndAnimationStart + incrementFloat}%,
        ${(100 - reappear.start) * currentCompression + finalItemEndAnimationStart}% {
          ${reappear.rules}
        }

        ${(100 - disappear.end) * currentCompression + finalItemEndAnimationStart}%,
        ${(100 - disappear.start) * currentCompression + finalItemEndAnimationStart}% {
          ${disappear.rules}
        }

        ${(100 - idle.end) * currentCompression + finalItemEndAnimationStart}%,
        ${(100 - idle.start) * currentCompression + finalItemEndAnimationStart - incrementFloat}% {
          ${idle.rules}
        }
      `;
    },
  ].reduce((acm, c) => {
    return `${acm}${c().styles.split('label:rules;').join('')}`;
  }, '');

  const finalCursorAnimation = keyframes`
    ${pulsesCss}
  `;

  const finalStaggerAnimation = keyframes` 
    ${interpolate(() => {
      return css`
        0%,
        ${finalItemDuration - incrementFloat}% {
          opacity: 1;
        }

        ${finalItemDuration}%,
        100% {
          opacity: 0;
        }
      `;
    })}
  `;

  return {
    staggerAnimation,
    cursorAnimation,
    typewriterAnimation,
    finalStaggerAnimation,
    finalCursorAnimation,
    finalTypewriterAnimation,
    pulseAnimation,
    pulseAnimationDurationMs,
    baseAnimationMs,
    totalAnimationMs,
    delays,
  };
};

export type ICtaAnimationData = ReturnType<typeof composeCtaAnimationData>;

export const START_ANIMATION_CLASS = 'animate';
